const REDIRECT_LOOP_INTERVAL_MILLIS = 8000;
const REDIRECT_LOOP_COUNT = 3;

type UsageReason = 'id site login' | 'id site onboarding' | 'missing user data';

const LOCAL_STORAGE_KEY = 'REDIRECT_LOOP_TIMESTAMPS';

const ROW_SEPARATOR = '\n';
const ITEM_SEPARATOR = '|';

interface RedirectTimestamp {
  date: Date;
  reason: UsageReason;
}

const timestampToString = (timestamp: RedirectTimestamp): string => {
  return `${timestamp.date.toISOString()}${ITEM_SEPARATOR}${timestamp.reason}`;
};

const timestampFromString = (str: string): RedirectTimestamp => {
  const [rawDate, rawReason] = str.split(ITEM_SEPARATOR);
  return {
    date: new Date(rawDate),
    reason: rawReason as UsageReason,
  };
};

/**
 * Save redirect timestamps into localstorage. Only keeps maximum of REDIRECT_LOOP_COUNT items
 */
const saveRedirectTimestamp = (timestamps: RedirectTimestamp[]) => {
  try {
    const raw = timestamps
      .splice(0, REDIRECT_LOOP_COUNT)
      .map(timestampToString)
      .join(ROW_SEPARATOR);
    window.localStorage.setItem(LOCAL_STORAGE_KEY, raw);
  } catch (error) {
    clearRedirectTimestamp();
  }
};

/**
 * Load redirect timestamps from localstorage.
 */
const loadRedirectTimestamp = (): RedirectTimestamp[] => {
  const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  const timestamps: RedirectTimestamp[] | undefined = raw
    ?.split(ROW_SEPARATOR)
    .map((rawTimestamp) => timestampFromString(rawTimestamp));
  return timestamps ?? [];
};

/**
 * Clear redirect timestamps from localstorage.
 */
function clearRedirectTimestamp() {
  window.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

/**
 * Check if application is in redirect loop based on a series of redirect timestamps.
 *
 * Check that all timestamps we have are within REDIRECT_LOOP_INTERVAL_MILLIS of each other.
 * If any of the timestamps are longer apart, then we are not in redirect loop.
 *
 * Ff we don't have REDIRECT_LOOP_COUNT number of timestamps, we are not in redirect loop.
 */
const isApplicationInRedirectLoop = (timestamps: RedirectTimestamp[]): boolean => {
  if (timestamps.length <= REDIRECT_LOOP_COUNT) {
    return false;
  }
  for (let i = 0; i < timestamps.length - 1; i += 1) {
    const current = timestamps[i];
    const next = timestamps[i + 1];
    const timeDiff = Math.abs(current.date.getTime() - next.date.getTime());
    if (Number.isNaN(timeDiff)) {
      // I don't think this should happen
      return false;
    }
    if (timeDiff > REDIRECT_LOOP_INTERVAL_MILLIS) {
      return false;
    }
  }
  return true;
};

/**
 * Utility for checking that the application is not in redirect loop.
 * This is done by allowing the loop to run a couple of times, and then intervening by throwing an error if the loop continues.
 *
 * Bookkeeping is done on localstorage by saving all the timestamps on moments where we're redirect away from the application.
 *
 * Opted for doing this based on multiple iterations of the loop because the single iteration in the redirect loop
 * between id-ui and dashboard-ui seems to take around 6~ seconds. This seems long enough that we could run into some user weird user doing weird user things.
 *
 */
export const assertNoRedirectLoop = (usageReason: UsageReason) => {
  const now = new Date();

  const timestamps: RedirectTimestamp[] = [
    { date: now, reason: usageReason },
    ...loadRedirectTimestamp(),
  ];

  if (isApplicationInRedirectLoop(timestamps)) {
    // eslint-disable-next-line no-console
    console.error(`Stuck in redirect loop, Stopping before attempt number ${timestamps.length}.`);
    clearRedirectTimestamp();
    const trace = timestamps.map((it) => `${it.reason} at ${it.date.toISOString()}`).join('\n');
    throw new Error(`application is in redirect loop. Caused by redirect stack:\n${trace}`);
  } else {
    saveRedirectTimestamp(timestamps);
  }
};
